// @ts-strict-ignore
import React, { useRef } from 'react';
import styled from 'styled-components';

import { FilestackImageData } from 'api/types/shared.types';
import { PublicPage } from 'api/website';
import { useContentSubsite } from 'cms/hooks';
import { Box } from 'components/Box';
import { Hero } from 'components/Hero';
import { SearchToolbar } from 'components/Search/SearchToolbar';
import { H1, RichText } from 'components/Text';
import HideContainer from 'containers/HideContainer';
import PostSearchInputContainer from 'containers/PostSearchInputContainer';
import {
  breakpoints,
  colors,
  fontSizesByType,
  gutterWidth,
  maxWidth,
  shadows,
  transition,
} from 'theme';
import { useSticky, useViewportBreakpointValue } from 'utils/screen';

const Title = styled.div<{ lineHeight?: number }>`
  font-size: ${fontSizesByType.h3[0]}px;
  @media (min-width: ${breakpoints[1]}) {
    font-size: ${fontSizesByType.h3[2]}px;
  }
`;
const FixedMenu = styled.div<{ $sticky?: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 100;
  width: 100%;
  box-shadow: ${shadows.toolbar};
  transition: all ${transition};
  will-change: transform;
  transform: ${({ $sticky }) =>
    $sticky ? 'translateY(0)' : 'translateY(-100%) translateY(-4px)'};
`;

type Props = {
  content: PublicPage;
  title?: string;
  image: FilestackImageData | null | undefined;
  imageMobile: FilestackImageData | null | undefined;
  showListingCounts?: boolean;
  searchMode?: boolean;
};

export function ArticleSearchHero({
  content,
  title,
  image,
  imageMobile,
  showListingCounts,
  searchMode,
}: Props) {
  const subsite = useContentSubsite({ content });

  const offsetTop = useViewportBreakpointValue({
    xs: 64,
    lg: 96 + (showListingCounts ? 80 : 0),
  });

  const menuRef = useRef<HTMLDivElement>(null);
  const menuIsSticky = useSticky({
    ref: menuRef,
    offset: { top: offsetTop },
  });

  return (
    <>
      <Hero
        pb={[48, null, null, 64]}
        pt={[48, null, null, 80]}
        mb={[120, null, null, 0]}
        minHeight="calc(100vh - 300px)"
        heroImage={image}
        mobileHeroImage={imageMobile}
        data-qa-id="search-hero"
      >
        {!searchMode && (
          // TODO: Fix this the next time the file is edited.
          // eslint-disable-next-line react/jsx-no-useless-fragment
          <>
            <Box
              transform={[
                'translateY(180px) translateZ(0)',
                null,
                null,
                'translateZ(0)',
              ]}
              width="100%"
              maxWidth={[400, null, null, maxWidth[3]]}
              px={[0, null, null, gutterWidth]}
              m="0 auto"
            >
              <Box
                width={[1, null, null, 1 / 2]}
                px={[20, null, null, 60]}
                py={[20, null, null, 40]}
                borderRadius={12}
                boxShadow="0 0 6px rgba(0,0,0,0.3)"
                bg="white"
                color={colors.greyishBrown}
              >
                <RichText mb={24}>
                  <H1>
                    <Title
                      lineHeight={1.3}
                      dangerouslySetInnerHTML={{
                        __html:
                          title && title.replace(/<(\/?)(p|h1)>/g, '<$1span>'),
                      }}
                    />
                  </H1>
                </RichText>
                <Box data-qa-id="article-search-hero-menu">
                  <PostSearchInputContainer
                    isStatic
                    homePageUrl={subsite ? subsite.homePage.url : ''}
                    searchPlaceholderText={
                      subsite && subsite.searchPlaceholderText
                        ? subsite.searchPlaceholderText
                        : undefined
                    }
                  />
                </Box>
              </Box>
            </Box>
          </>
        )}
        {/* {cta} */}
      </Hero>

      <div ref={menuRef}>
        <HideContainer xs sm md>
          <Box data-qa-id="article-search-hero-menu">
            <FixedMenu $sticky={menuIsSticky}>
              <SearchToolbar content={content} />
            </FixedMenu>
          </Box>
        </HideContainer>
      </div>
    </>
  );
}
